import React from 'react';
import PropTypes from 'prop-types';
import CwDropdownMulti from '../cwdropdown/CwDropdownMulti';
import CwPillTriggerField from '../cwpilltriggerfield/CwPillTriggerField';
import CwPill from '../cwpill/CwPill';
import { setMenuPositions, calcDropdownPosition } from '../../utils/CwDropdownService';
import css from './CwMultiSelect.scss';

/**
 * Based on react-autocomplete:
 * https://github.com/reactjs/react-autocomplete
 */

class CwMultiSelect extends React.Component {
  constructor(props) {
    super(props);

    this._ignoreBlur = false;
    this._ignoreScroll = false;
    this._performAutoCompleteOnUpdate = false;
    this._performAutoCompleteOnKeyUp = false;

    this.state = {
      displayText: '',
      expanded: false,
      highlightedIndex: null,
      filter: true,
      focused: false
    };

    this.keyDownHandlers = {
      ArrowDown(event) {
        // Cycle through list of items on arrow down, updating `highlightedIndex` along the way 
        event.preventDefault(); // prevent window scrolling
        const itemsLength = this.getFilteredItems().length;
        if (!itemsLength) return;
        const { highlightedIndex } = this.state;
        let index = (
          highlightedIndex === null ||
          highlightedIndex === itemsLength - 1
        ) ? -1 : highlightedIndex + 1; // Go back to the top when reaching the end
        this._performAutoCompleteOnKeyUp = true;
        this.setState({
          highlightedIndex: index,
          expanded: true,
          filter: true
        });
      },

      ArrowUp(event) {
        // Cycle through list of items on arrow up, updating `highlightedIndex` along the way
        event.preventDefault(); // prevent window scrolling
        const itemsLength = this.getFilteredItems().length;
        if (!itemsLength) return;
        const { highlightedIndex } = this.state;
        let index = (
          highlightedIndex === -1 ||
          highlightedIndex === null
        ) ? itemsLength - 1 : highlightedIndex - 1; // Go back to the end when reaching the top
        this._performAutoCompleteOnKeyUp = true;
        this.setState({
          highlightedIndex: index,
          expanded: true,
          filter: true
        });
      },

      Backspace() {
        let values = this.props.value;
        if (this.state.displayText === '') {
          if (values.length !== this.filterExcludedItems(this.props.items).length) {
            values.pop();
          } else {
            values.splice(0,values.length); //clear the array
          }
          this.props.onChange(values);
        }
      },

      Enter(event) {
        if (this.state.expanded === false) {
          // Menu is closed so there is no selection to accept: do nothing
        }
        else {
          // Text entered, menu item has been highlighted, and Enter is hit: select menu item
          event.preventDefault();
          let item = this.getFilteredItems()[this.state.highlightedIndex];
          this.selectItem(item);
          this.setState({
            highlightedIndex: null
          });
        }
      },

      Escape() {
        this.setState({
          highlightedIndex: null,
          expanded: false
        });
      },
    };
  }

  componentWillMount() {
    window.addEventListener('scroll', this.handleScroll, true);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll, true);
  }

  componentWillReceiveProps(nextProps) {
    this._performAutoCompleteOnUpdate = true;
    // If `items` has changed we want to reset `highlightedIndex` since it probably no longer refers to a relevant item
    if (this.props.items !== nextProps.items ||
      // The entries in `items` may have been changed even though the object reference remains the same, double check by seeing if `highlightedIndex` points to an existing item
      this.state.highlightedIndex >= nextProps.items.length) {
      this.setState({ highlightedIndex: null });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.expanded === true && prevState.expanded === false) {
      const menuPositions = setMenuPositions(this.cwPillTriggerField.getParentDiv());
      this.setState({
        menuTop: menuPositions.menuTop,
        menuLeft: menuPositions.menuLeft,
        menuWidth: menuPositions.menuWidth,
        inputHeight: menuPositions.inputHeight
      });
    }

    if (this.state.expanded && this._performAutoCompleteOnUpdate) {
      this._performAutoCompleteOnUpdate = false;
      this.autoCompleteText();
    }

    if (prevState.expanded !== this.state.expanded) {
      if (this.state.expanded) {
        if (this.props.onExpand)
          this.props.onExpand(this.state.expanded);
      }
      else {
        if (this.props.onCollapse)
          this.props.onCollapse(this.state.expanded);
        let items = this.getFilteredItems();
        if (items.length === 0 || this.state.displayText === '') { // If text entered doesn't match any valid items or is empty
          this.setState({
            displayText: ''
          });
        }
      }
    }
  }

  shouldItemRender(item, enteredText) {
    // Only render items that match up to what has been typed
    return (
      enteredText.toLowerCase() === item.name.substring(0, enteredText.length).toLowerCase()
    );
  }

  filterExcludedItems(items) {
    if (this.props.excludedItems) {
      items = items.filter((item) => (
        !this.props.excludedItems.includes(item.id)
      ));
    }

    return items;
  }

  getFilteredItems() {
    let items = this.props.items;

    items = this.filterExcludedItems(items);

    if (this.shouldItemRender && this.state.filter) {
      items = items.filter((item) => (
        this.shouldItemRender(item, this.state.displayText)
      ));
    }

    return items;
  }

  autoCompleteText() {
    if (this.state.displayText === '')
      return;
    let { highlightedIndex } = this.state;
    let items = this.getFilteredItems();
    if (items.length === 0)
      return;
    let matchedItem = highlightedIndex !== null ?
      items[highlightedIndex] : items[0];
    let itemValue = matchedItem.name;
    let itemValueDoesMatch = (itemValue.toLowerCase().indexOf(
      this.state.displayText.toLowerCase()
    ) === 0);
    if (itemValueDoesMatch && highlightedIndex === null)
      this.setState({ highlightedIndex: 0 });
  }

  highlightItemFromMouse = (index) => {
    this.setState({ highlightedIndex: index });
  };

  findName(id, items) {
    var i;
    for (i = 0; i < items.length; i++) {
      if (items[i].id === id) {
        return items[i].name;
      }
    }
  }

  findValue(id, values) {
    var i;
    for (i = 0; i < values.length; i++) {
      if (values[i] === id) {
        return i;
      }
    }
    return -1;
  }

  selectItem = (item) => {
    let values = this.props.value.slice();

    if (item.id === -1 && values.length !== this.filterExcludedItems(this.props.items).length) { // Select All
      this.getFilteredItems().forEach(function (element) {
        if (this.findValue(element.id, values) === -1) {
          values.push(element.id);
        }
      }, this);
    }
    else if (item.id === -1 && values.length === this.filterExcludedItems(this.props.items).length) { // Deselect All
      this.getFilteredItems().forEach(function (element) {
        let valuePosition = this.findValue(element.id, values);
        if (valuePosition !== -1) {
          values.splice(valuePosition, 1);
        }
      }, this);
    }
    else {
      let selection = item;
      let valuePosition = this.findValue(selection.id, values);

      if (valuePosition === -1) {
        values.push(selection.id);
      }
      else {
        values.splice(valuePosition, 1);
      }
    }

    this.setState({
      displayText: ''
    });

    this.props.onChange(values); // Report values selected
    this.focus();
  };

  setIgnoreBlur = (ignore) => {
    this._ignoreBlur = ignore;
  };

  handleDropdownEnter = () => {
    this._ignoreScroll = true;
  };

  handleDropdownLeave = () => {
    this._ignoreScroll = false;
  };

  handleScroll = (event) => {
    // Hide dropdown when window is scrolled
    if (this.state.expanded && (event.target === document || !event.target.className.includes('CwDropdownMulti-dropDown'))) {
      this.setState({
        expanded: false,
        prevExpanded: true
      });
    }
  };

  handleKeyDown = (event) => {
    if (this.keyDownHandlers[event.key])
      this.keyDownHandlers[event.key].call(this, event);
  };

  handleKeyUp = () => {
    if (this._performAutoCompleteOnKeyUp) {
      this._performAutoCompleteOnKeyUp = false;
      this.autoCompleteText();
    }
  };

  handleChange = (displayText) => {
    this._performAutoCompleteOnKeyUp = true;
    this.setState({
      displayText: displayText,
      expanded: true,
      filter: true,
    });
  };

  handleInputBlur = () => {
    if (this._ignoreBlur)
      return;
    // IE fires a blur event when clicking the scrollbar - this prevents the dropdown from closing
    if(this.cwDropdown && this.cwDropdown.isMousedOver()) {
      this.cwPillTriggerField.focus();
      return;
    }
    this.setState({
      expanded: false,
      focused: false,
      highlightedIndex: null
    });
    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };

  handleInputFocus = () => {
    if (this._ignoreBlur) {
      this.setIgnoreBlur(false);
      return;
    }
    this.setState({
      focused: true
    });
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  handleTriggerClick = () => {
    // Expand the full unfiltered dropdown if the trigger is clicked
    if (this.props.disabled)
      return;
    this.focus();
    this.setState(
      {
        expanded: true,
        filter: false
      }
    );
  };

  closePill = (key) => {
    if(key.id === 'all'){
      this.selectItem({id: -1}); // deselect all
    }
    else {
      let values = this.props.value;
      let valuePosition = this.findValue(key.id, values);
      values.splice(valuePosition, 1);
      this.props.onChange(values); // Report values selected
    }
    this.focus();
  };

  focus() {
    this.cwPillTriggerField.focus();
  }

  render() {
    const dropdownSettings = {
      top: this.state.menuTop,
      itemHeight: 31,
      inputHeight: this.state.inputHeight,
      maxItemsPerPage: 10,
      numOfItems: this.getFilteredItems().length,
      numOfExtraItems: 1
    };

    const dropdownPosition = calcDropdownPosition(dropdownSettings);

    let style = { // Pass calculated position coordinates to dropdown with inline style
      left: this.state.menuLeft,
      top: dropdownPosition.top,
      minWidth: this.state.menuWidth,
      height: dropdownPosition.menuHeight
    };

    let values = this.props.value;
    let zeroPosition = this.findValue(0, values);
    if (zeroPosition !== -1) {
      values.splice(zeroPosition, 1);
    }
    let pills = [];
    let allSelected = false;
    let noneSelected = false;

    if (values.length === this.filterExcludedItems(this.props.items).length && values.length > 0) {
      pills.push(<CwPill
                    id='all'
                    key='all'
                    value={this.props.allPillText}
                    onDeletePill={this.closePill}
                    deletePillIcon={this.props.closePillIcon}
                  />);
      allSelected = true;
    }
    else if (values.length === 0) {
      noneSelected = true;
    }
    else {
      values.forEach(function (element) {
        pills.push(
          <CwPill
            id={element}
            key={element}
            value={this.findName(element, this.props.items) + ' '}
            onDeletePill={this.closePill}
            deletePillIcon={this.props.closePillIcon} />
        );
      }, this);
    }

    let cwMultiSelectStyle;

    if (this.props.invalid) {
      cwMultiSelectStyle = css.cwMultiSelectStyleInvalid;
    }
    else if (this.props.disabled) {
      cwMultiSelectStyle = css.cwMultiSelectStyleDisabled;
    }
    else {
      cwMultiSelectStyle = this.state.focused ? css.cwMultiSelectStyleFocused : css.cwMultiSelectStyle;
    }

    return (
      <CwPillTriggerField
        cwId={this.props.cwId}
        style={cwMultiSelectStyle}
        ref={(input) => this.cwPillTriggerField = input}
        triggerIcon={this.props.triggerIcon}
        onFocus={this.handleInputFocus}
        onBlur={this.handleInputBlur}
        onChange={this.handleChange}
        onKeyDown={this.handleKeyDown}
        onKeyUp={this.handleKeyUp}
        disabled={this.props.disabled}
        invalid={this.props.invalid}
        handleTriggerClick={this.handleTriggerClick}
        pills={this.props.disabled ? [] : pills}
        value={this.state.displayText}
      >
        {this.state.expanded && this.getFilteredItems().length > 0
          ? <CwDropdownMulti
            items={this.getFilteredItems()}
            value={this.props.value}
            highlightedIndex={this.state.highlightedIndex}
            style={style}
            onClick={this.selectItem}
            onMouseEnter={this.highlightItemFromMouse}
            onMouseDown={this.setIgnoreBlur} // Ignore blur to prevent menu from de-rendering before we can process click
            onMouseUp={this.setIgnoreBlur(false)}
            filter={this.state.filter}
            pickerText={this.props.pickerText}
            allSelected={allSelected}
            noneSelected={noneSelected}
            selectAllText={this.props.selectAllText}
            ref={(dropdown) => this.cwDropdown = dropdown}
          />
          : null}
      </CwPillTriggerField>
    );
  }
}

CwMultiSelect.propTypes = {
  /**
   * Connectwise element id to be used as unique identifier
   */
  cwId: PropTypes.string,
  /**
   * Path to trigger icon
   */
  triggerIcon: PropTypes.string,
  /**
   * Path to pill close icon
   */
  closePillIcon: PropTypes.string,
  /**
   * Formatted value for 'All' based on current locale
   */
  allPillText: PropTypes.string.isRequired,
  /**
   * Array of items to be listed in the dropdown menu. Must contain at least "id" and "name" fields.
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    })
  ),
  /**
   * Represents the item that has been selected from the list (or string of custom item entered in if allowCustomEntry set to true). Returns an object {id,name} by default.
   */
  value: PropTypes.any,
  /**
   * Callback function that is fired when combobox's selection changes
   */
  onChange: PropTypes.func,
  /**
   * Callback function that is fired when combobox's dropdown menu is expanded
   */
  onExpand: PropTypes.func,
  /**
   * Callback function that is fired when combobox's dropdown menu is collapsed
   */
  onCollapse: PropTypes.func,
  /**
   * Used to control if combobox is valid
   */
  invalid: PropTypes.bool,
  /**
   * Used to disable combobox
   */
  disabled: PropTypes.bool,
  /**
   * Callback function that is fired when the component is focused
   */
  onFocus: PropTypes.func,
  /**
   * Callback function that is fired when the component is blurred
   */
  onBlur: PropTypes.func,
  /**
   * Localized text for the Select All option
   */
  selectAllText: PropTypes.string.isRequired,
  /**
   * Items to be excluded from the dropdown
   */
  excludedItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))
};

CwMultiSelect.defaultProps = {
  value: []
};

export default CwMultiSelect;


// WEBPACK FOOTER //
// ./src/components/cwmultiselect/CwMultiSelect.jsx